import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { openCookiePreferences } from "@/components/CookieConsent";

/**
 * Public landing/legal footer. Three link groups + brand.
 * Used on Landing + every public legal page so the experience stays consistent.
 *
 * Bilingual: pass `lang="en"` for English copy; defaults to Turkish so all
 * existing legal pages keep working unchanged.
 */
const FOOTER_COPY = {
  en: {
    tagline:
      "uSeen is the AI visibility platform that helps local businesses get found when people ask AI assistants for recommendations.",
    groups: {
      product: "Product",
      resources: "Resources",
      legal: "Legal",
      account: "Account",
    },
    links: {
      howItWorks: "How it works",
      pricing: "Pricing",
      score: "AI Visibility Score",
      contact: "Contact",
      aiVisibility: "What is AI Visibility?",
      geo: "What is GEO?",
      useenHow: "How uSeen Works",
      faq: "FAQ",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      kvkk: "KVKK Notice",
      cookies: "Cookie Policy",
      consent: "Explicit Consent",
      cookiePrefs: "Cookie Preferences",
      login: "Log in",
      register: "Sign up",
    },
    urls: {
      aiVisibility: "/en/what-is-ai-visibility",
      geo: "/en/what-is-geo",
      useenHow: "/en/how-it-works",
      terms: "/en/terms-of-service",
      privacy: "/en/privacy-policy",
      kvkk: "/en/kvkk-notice",
      cookies: "/en/cookie-policy",
      consent: "/en/explicit-consent",
    },
    copyright: "uSeen — AI Visibility Platform",
  },
  tr: {
    tagline:
      "uSeen, yerel işletmelerin yapay zekâ asistanları tarafından bulunmasını kolaylaştıran AI görünürlük platformudur.",
    groups: {
      product: "Ürün",
      resources: "Kaynaklar",
      legal: "Yasal",
      account: "Hesap",
    },
    links: {
      howItWorks: "Nasıl Çalışır?",
      pricing: "Fiyatlandırma",
      score: "AI Görünürlük Skoru",
      contact: "İletişim",
      aiVisibility: "AI Visibility Nedir?",
      geo: "GEO Nedir?",
      useenHow: "uSeen Nasıl Çalışır?",
      faq: "SSS",
      terms: "Kullanım Koşulları",
      privacy: "Gizlilik Politikası",
      kvkk: "KVKK Aydınlatma Metni",
      cookies: "Çerez Politikası",
      consent: "Açık Rıza Metni",
      cookiePrefs: "Çerez Tercihleri",
      login: "Giriş Yap",
      register: "Kayıt Ol",
    },
    urls: {
      aiVisibility: "/ai-visibility-nedir",
      geo: "/geo-nedir",
      useenHow: "/nasil-calisir",
      terms: "/kullanim-kosullari",
      privacy: "/gizlilik-politikasi",
      kvkk: "/kvkk-aydinlatma-metni",
      cookies: "/cerez-politikasi",
      consent: "/acik-riza-metni",
    },
    copyright: "uSeen — AI Görünürlük Platformu",
  },
};

export default function SiteFooter({ lang = "tr" }) {
  const t = FOOTER_COPY[lang] || FOOTER_COPY.tr;
  // When lang === "en" the landing hero/anchors live on `/`; otherwise on `/tr`.
  const home = lang === "en" ? "/" : "/tr";
  return (
    <footer
      className="relative z-10 px-6 lg:px-12 pt-14 pb-10 border-t border-white/5 text-sm text-slate-400"
      data-testid="site-footer"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4"><Logo size="sm" /></div>
          <p className="text-xs text-slate-500 leading-relaxed max-w-xs">{t.tagline}</p>
        </div>

        <FooterGroup title={t.groups.product}>
          <FooterLink to={`${home}#nasil-calisir`} testid="footer-nasil-calisir">{t.links.howItWorks}</FooterLink>
          <FooterLink to="/fiyatlandirma" testid="footer-fiyatlandirma">{t.links.pricing}</FooterLink>
          <FooterLink to={`${home}#ai-gorunurluk-skoru`} testid="footer-skor">{t.links.score}</FooterLink>
          <FooterLink to="/iletisim" testid="footer-iletisim">{t.links.contact}</FooterLink>
        </FooterGroup>

        <FooterGroup title={t.groups.resources}>
          <FooterLink to={t.urls.aiVisibility} testid="footer-ai-visibility-nedir">{t.links.aiVisibility}</FooterLink>
          <FooterLink to={t.urls.geo} testid="footer-geo-nedir">{t.links.geo}</FooterLink>
          <FooterLink to={t.urls.useenHow} testid="footer-nasil-calisir-page">{t.links.useenHow}</FooterLink>
          <FooterLink to={`${home}#faq`} testid="footer-faq">{t.links.faq}</FooterLink>
        </FooterGroup>

        <FooterGroup title={t.groups.legal}>
          <FooterLink to={t.urls.terms} testid="footer-kullanim-kosullari">{t.links.terms}</FooterLink>
          <FooterLink to={t.urls.privacy} testid="footer-gizlilik">{t.links.privacy}</FooterLink>
          <FooterLink to={t.urls.kvkk} testid="footer-kvkk">{t.links.kvkk}</FooterLink>
          <FooterLink to={t.urls.cookies} testid="footer-cerez">{t.links.cookies}</FooterLink>
          <FooterLink to={t.urls.consent} testid="footer-acik-riza">{t.links.consent}</FooterLink>
          <li>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="text-slate-400 hover:text-cyan-300 transition-colors text-left"
              data-testid="footer-cookie-prefs"
            >
              {t.links.cookiePrefs}
            </button>
          </li>
        </FooterGroup>

        <FooterGroup title={t.groups.account}>
          <FooterLink to="/giris" testid="footer-giris">{t.links.login}</FooterLink>
          <FooterLink to="/kayit" testid="footer-kayit">{t.links.register}</FooterLink>
        </FooterGroup>
      </div>

      <div className="max-w-6xl mx-auto mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div>useen.ai</div>
        <div>© {new Date().getFullYear()} {t.copyright}</div>
      </div>
    </footer>
  );
}

function FooterGroup({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">{title}</div>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children, testid }) {
  return (
    <li>
      <Link to={to} className="text-slate-400 hover:text-cyan-300 transition-colors" data-testid={testid}>
        {children}
      </Link>
    </li>
  );
}
